// /dashboard/moderation?tab=developers: founding developer applications for moderators (docs/build/founding-devs.md
// "The internal score", "Enrichment"), plus the invite maker and the invites list, which is the owner's outreach
// tracker. The cards come sorted by score, and every part of the score is shown so a moderator can see why someone
// ranks where they do. The X numbers are typed in by hand: nothing here calls X. Accounts is the authority on who's a
// moderator: its list answers 403 for everyone else.

import { api, ApiError, isMissingEndpoint } from "./api.ts";
import { artUrl, getCatalogue, type Catalogue } from "./catalogue.ts";
import { coverStyle } from "./components.ts";
import {
  byScore,
  domainOf,
  EVIDENCE_LABEL,
  optionLabel,
  readCount,
  readHandle,
  SCORE_PARTS,
  STATUS_COPY,
  URL_STATE,
  type InviteRow,
  type ModCard,
  type Status,
} from "./dev-apply.ts";
import { clear, errorBox, fmtDate, h, link, toast } from "./dom.ts";

/** Asked once per user per page load. */
let probe: { id: string; ok: Promise<boolean> } | null = null;

export function isDevModerator(userId: string): Promise<boolean> {
  if (probe?.id !== userId) probe = { id: userId, ok: api("accounts", "/dev-applications?status=submitted").then(() => true, () => false) };
  return probe.ok;
}

/** Reports | Developers above the moderation view, when this moderator has both. */
export function modTabs(view: HTMLElement, tab: string | null, reports: boolean, developers: boolean): HTMLElement {
  if (!(reports && developers)) return view;
  const t = (href: string, text: string, on: boolean) => link(href, { class: `pill${on ? " on" : ""}`, "aria-current": on ? "page" : null }, text);
  return h("div", null, h("nav", { class: "devmod-tabs", "aria-label": "Moderation" }, t("/dashboard/moderation", "Reports", tab !== "developers"), t("/dashboard/moderation?tab=developers", "Developers", tab === "developers")), view);
}

const STATUSES: [Status, string][] = [
  ["submitted", "To review"],
  ["waitlisted", "Waitlisted"],
  ["approved", "Approved"],
  ["declined", "Declined"],
];

export function developersView(): HTMLElement {
  const list = h("div", { "aria-live": "polite" }, h("p", { class: "dim" }, "Loading…"));
  const filters = h("div", { class: "devmod-filters", role: "group", "aria-label": "Status" });
  let status: Status = "submitted";
  let cat: Catalogue | null = null;
  const load = async () => {
    clear(filters, STATUSES.map(([s, label]) => h("button", { type: "button", class: `pill${s === status ? " on" : ""}`, "aria-pressed": s === status ? "true" : "false", onclick: () => ((status = s), void load()) }, label)));
    let cards: ModCard[];
    try {
      [cards, cat] = await Promise.all([api<{ applications: ModCard[] }>("accounts", `/dev-applications?status=${status}`).then((r) => byScore(r.applications)), cat ? Promise.resolve(cat) : getCatalogue().catch(() => null)]);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) return clear(list, h("p", null, "Only Banger moderators review founding developer applications."));
      if (isMissingEndpoint(e)) return clear(list, h("p", { class: "dim" }, "Applications open once accounts has the founding developer routes."));
      return clear(list, errorBox(e));
    }
    clear(
      list,
      h("p", { class: "dim small" }, `${cards.length} ${cards.length === 1 ? "application" : "applications"}, best score first. The score is a sort key, not a verdict.`),
      cards.length ? h("ol", { class: "devmod-cards" }, cards.map((c, i) => card(c, i + 1, cat, (fresh) => {
        if (fresh.status !== status) toast(`${fresh.displayName}: ${STATUS_COPY[fresh.status].badge.toLowerCase()}`);
        void load();
      }))) : h("p", { class: "devmod-empty" }, status === "submitted" ? "Nothing to review. Make an invite below." : "None yet."),
    );
  };
  void load();
  return h(
    "div",
    { class: "devmod" },
    h("h1", null, "Developers"),
    h("p", { class: "dim" }, "Founding developer applications. Approve, waitlist or decline, with a note the applicant sees. Approving approves their claims and queues their new addresses."),
    filters,
    list,
    inviteMaker(),
  );
}

// ---- one application ---------------------------------------------------------------------------------------------

function card(c: ModCard, rank: number, cat: Catalogue | null, changed: (c: ModCard) => void): HTMLElement {
  const out = h("div", { "aria-live": "polite" });
  const x = readHandle(c.xHandle ?? "");
  const full = (id: string) => cat?.games.find((g) => g.id === id);
  const followers = h("input", { name: "xFollowers", inputmode: "numeric", autocomplete: "off", placeholder: "e.g. 12.4k", value: c.enrichment.x?.followers ?? "", "aria-label": "X followers" }) as HTMLInputElement;
  const likes = h("input", { name: "xLikes", inputmode: "numeric", autocomplete: "off", placeholder: "e.g. 300", value: c.enrichment.x?.likes ?? "", "aria-label": "Typical likes per game post" }) as HTMLInputElement;
  // the moderator's own 1–5 after playing (craft counts it)
  const rating = h("select", { name: "rating", "aria-label": "Your rating after playing" }, h("option", { value: "" }, "Not played"), [1, 2, 3, 4, 5].map((n) => h("option", { value: String(n), selected: c.enrichment.rating?.value === n }, `${n} / 5`))) as HTMLSelectElement;
  const note = h("textarea", { name: "note", rows: 2, placeholder: "A note the applicant sees (optional)", "aria-label": "Note to the applicant" }) as HTMLTextAreaElement;
  if (c.note) note.value = c.note;
  const post = async (path: string, body: unknown, btn: HTMLButtonElement) => {
    btn.setAttribute("disabled", "");
    clear(out);
    try {
      changed((await api<{ application: ModCard }>("accounts", `/dev-applications/${encodeURIComponent(c.id)}/${path}`, { body })).application);
    } catch (e) {
      btn.removeAttribute("disabled");
      clear(out, errorBox(e));
    }
  };
  const save = h("button", { type: "button", class: "btn small devmod-save" }, "Save");
  save.addEventListener("click", () => {
    const f = readCount(followers.value);
    const l = readCount(likes.value);
    if (f === undefined || l === undefined) return clear(out, h("p", { class: "error", role: "alert" }, "Type a number, like 12400 or 12.4k."));
    void post("enrich", { xFollowers: f, xLikes: l, rating: rating.value ? Number(rating.value) : null }, save);
  });
  const decide = (path: "approve" | "waitlist" | "decline", label: string, cls: string) => {
    const b = h("button", { type: "button", class: `btn small devmod-decide ${cls}` }, label);
    b.addEventListener("click", () => void post(path, { note: note.value.trim() || undefined }, b));
    return b;
  };
  return h(
    "li",
    { class: "devmod-card", "data-id": c.id },
    h(
      "div",
      { class: "devmod-head" },
      h("span", { class: "devmod-rank" }, `#${rank}`),
      h(
        "div",
        { class: "devmod-who" },
        h("h2", null, c.displayName),
        h(
          "p",
          { class: "small dim" },
          x ? h("a", { href: `https://x.com/${x}`, target: "_blank", rel: "noopener noreferrer" }, `@${x}`) : "no X handle",
          c.user.handle ? ` · account @${c.user.handle}` : "",
          c.user.email ? ` · ${c.user.email}` : "",
          c.site ? [" · ", h("a", { href: /^https?:/i.test(c.site) ? c.site : `https://${c.site}`, target: "_blank", rel: "noopener noreferrer" }, domainOf(c.site))] : "",
          c.studio ? ` · ${optionLabel("studio", c.studio)}` : "",
          ` · applied ${fmtDate(c.createdAt)}`,
        ),
        h("p", { class: "small" }, h("span", { class: `badge ${c.xEvidence === "none" || c.xEvidence === "game_page" ? "warn" : "ok"}` }, EVIDENCE_LABEL[c.xEvidence] ?? c.xEvidence), c.invite ? h("span", { class: "dim" }, ` invite ${c.invite.code}`) : null),
      ),
      h("div", { class: "devmod-total", "aria-label": `Score ${c.score?.total ?? 0} of 100` }, h("strong", null, String(c.score?.total ?? "–")), h("span", null, "/100")),
    ),
    h(
      "div",
      { class: "devmod-score" },
      SCORE_PARTS.map(([key, label]) => {
        const p = c.score?.[key];
        return h(
          "div",
          { class: "devmod-part" },
          h("div", { class: "devmod-part-head" }, h("span", null, label), h("span", null, p ? `${p.points}/${p.max}` : "–")),
          h("div", { class: "devmod-bar", "aria-hidden": "true" }, h("span", { style: { width: p && p.max ? `${Math.round((100 * p.points) / p.max)}%` : "0%" } })),
          p?.why.length ? h("p", { class: "small dim" }, p.why.join(" · ")) : null,
        );
      }),
    ),
    c.creators.length || c.urls.length
      ? h(
          "div",
          { class: "devmod-games" },
          c.creators.flatMap((cr) =>
            cr.games.map((g) => {
              const f = full(g.id);
              const art = artUrl(g.art);
              const held = cr.state === "claimed_elsewhere" ? "Held by another account" : cr.state === "withdrawn" ? "Dropped" : null;
              return link(`/g/${g.id}`, { class: `devmod-game${held ? " held" : ""}`, target: "_blank" }, h("span", { class: "devmod-game-art", style: f ? coverStyle(f) : { backgroundImage: art ? `url("${art}")` : "" } }), h("span", null, g.title, held ? h("em", null, held) : null));
            }),
          ),
          c.urls.filter((u) => u.state === "being_added").map((u) => h("a", { class: "devmod-game new", href: /^https?:/i.test(u.url) ? u.url : `https://${u.url}`, target: "_blank", rel: "noopener noreferrer" }, h("span", { class: "devmod-game-art blank" }, domainOf(u.url).slice(0, 1).toUpperCase()), h("span", null, domainOf(u.url), h("em", null, URL_STATE[u.state] ?? u.state)))),
        )
      : h("p", { class: "small dim" }, "No games claimed or added."),
    h(
      "dl",
      { class: "devmod-answers" },
      h("dt", null, "Players find them"),
      h("dd", null, c.answers.discovery.map((d) => optionLabel("discovery", d)).join(", ") || "–"),
      h("dt", null, "What's next"),
      h("dd", null, [c.answers.next.when ? optionLabel("when", c.answers.next.when) : "–", c.answers.next.what ? `: ${c.answers.next.what}` : ""].join("")),
      h("dt", null, "Money so far"),
      h("dd", null, c.answers.money ? optionLabel("money", c.answers.money) : "–"),
    ),
    h(
      "div",
      { class: "devmod-x" },
      h("label", { class: "field" }, h("span", { class: "small dim" }, "X followers"), followers),
      h("label", { class: "field" }, h("span", { class: "small dim" }, "Likes per game post"), likes),
      save,
      h("label", { class: "field" }, h("span", { class: "small dim" }, "Your rating"), rating),
      x ? h("a", { class: "small", href: `https://x.com/${x}`, target: "_blank", rel: "noopener noreferrer" }, "Open the profile") : null,
    ),
    h(
      "div",
      { class: "devmod-decision" },
      c.status !== "submitted" ? h("p", { class: "small" }, h("span", { class: "badge" }, STATUS_COPY[c.status].badge), c.decidedAt ? h("span", { class: "dim" }, ` ${fmtDate(c.decidedAt)}`) : null) : null,
      note,
      // approve and decline from submitted or waitlisted; waitlist only from submitted (accounts refuses the rest)
      c.status === "submitted" || c.status === "waitlisted"
        ? h("div", { class: "actions" }, decide("approve", "Approve", ""), c.status === "submitted" ? decide("waitlist", "Waitlist", "ghost") : null, decide("decline", "Decline", "danger"))
        : null,
    ),
    out,
  );
}

// ---- the invite maker and the outreach tracker -------------------------------------------------------------------

type Pick = { kind: "creator" | "game"; id: string; label: string };

function inviteMaker(): HTMLElement {
  const picked: Pick[] = [];
  const chips = h("div", { class: "invite-picked" });
  const hits = h("div", { class: "invite-hits" });
  const made = h("div", { class: "invite-made", "aria-live": "polite" });
  const list = h("div", { class: "invite-list" });
  const err = h("div", { "aria-live": "polite" });
  const name = h("input", { name: "name", required: true, autocomplete: "off", maxlength: 60 }) as HTMLInputElement;
  const x = h("input", { name: "xHandle", required: true, autocomplete: "off", autocapitalize: "off", placeholder: "@handle" }) as HTMLInputElement;
  const note = h("textarea", { name: "note", rows: 2, placeholder: "Where you found them, what you liked (only moderators see it)" }) as HTMLTextAreaElement;
  let cat: Catalogue | null = null;
  // the tracker's rows: a second invite to the same handle asks first
  let sentRows: InviteRow[] = [];
  let dupOk: string | null = null;
  void getCatalogue().then((c) => (cat = c), () => {});
  const drawChips = () =>
    clear(
      chips,
      picked.map((p, i) => h("span", { class: "invite-chip" }, h("span", { class: "dim small" }, p.kind === "creator" ? "Creator " : "Game "), p.label, h("button", { type: "button", "aria-label": `Remove ${p.label}`, onclick: () => (picked.splice(i, 1), drawChips()) }, "×"))),
    );
  const search = h("input", {
    type: "search",
    autocomplete: "off",
    placeholder: "Find their games or creator listing",
    "aria-label": "Search the catalogue",
    oninput: () => {
      const q = search.value.trim().toLowerCase();
      if (!cat || q.length < 2) return clear(hits);
      const creators = cat.creators.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 4);
      const games = cat.games.filter((g) => g.title.toLowerCase().includes(q)).slice(0, 6);
      const add = (p: Pick) => () => {
        if (!picked.some((x) => x.kind === p.kind && x.id === p.id)) picked.push(p);
        search.value = "";
        clear(hits);
        drawChips();
      };
      clear(
        hits,
        creators.map((c) => h("button", { type: "button", class: "invite-hit", onclick: add({ kind: "creator", id: c.id, label: c.name }) }, h("strong", null, c.name), h("span", { class: "dim small" }, ` creator · ${cat!.games.filter((g) => g.creator.id === c.id).length} games`))),
        games.map((g) => h("button", { type: "button", class: "invite-hit", onclick: add({ kind: "game", id: g.id, label: g.title }) }, h("span", { class: "invite-hit-art", style: coverStyle(g) }), h("strong", null, g.title), h("span", { class: "dim small" }, ` by ${g.creator.name}`))),
        !creators.length && !games.length ? h("p", { class: "dim small" }, "Nothing listed matches. Make the invite anyway: they can add addresses themselves.") : null,
      );
    },
  }) as HTMLInputElement;
  const submit = h("button", { type: "submit", class: "btn" }, "Make the link");
  const form = h(
    "form",
    {
      class: "invite-form",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        clear(err);
        const handle = readHandle(x.value);
        if (!name.value.trim()) return void clear(err, h("p", { class: "error", role: "alert" }, "Their name, please."));
        if (!handle) return void clear(err, h("p", { class: "error", role: "alert" }, "That X handle doesn't look right."));
        const before = sentRows.find((r) => r.xHandle.toLowerCase() === handle.toLowerCase());
        if (before && dupOk !== handle.toLowerCase()) {
          dupOk = handle.toLowerCase();
          return void clear(err, h("p", { class: "error", role: "alert" }, `@${handle} already has an invite from ${fmtDate(before.createdAt)}${before.used ? ", used" : ", not used yet"}. Copy that one below, or press Make the link again for a new one.`));
        }
        submit.setAttribute("disabled", "");
        try {
          const r = await api<{ code: string; url: string }>("accounts", "/dev-invites", {
            body: { name: name.value.trim(), xHandle: handle, creatorIds: picked.filter((p) => p.kind === "creator").map((p) => p.id), gameIds: picked.filter((p) => p.kind === "game").map((p) => p.id), note: note.value.trim() || undefined },
          });
          const url = new URL(r.url, location.origin).href;
          clear(made, h("p", { class: "small dim" }, `Invite for ${name.value.trim()} (@${handle}):`), h("div", { class: "invite-link" }, h("a", { href: url, target: "_blank", rel: "noopener" }, url), copyButton(url)));
          form.reset();
          picked.length = 0;
          drawChips();
          void loadList();
        } catch (x) {
          clear(err, errorBox(x));
        } finally {
          submit.removeAttribute("disabled");
        }
      },
    },
    h("div", { class: "invite-row" }, h("label", { class: "field" }, h("span", null, "Name"), name), h("label", { class: "field" }, h("span", null, "X handle"), x)),
    h("div", { class: "field" }, h("span", null, "Their games or creator listing"), search, hits, chips),
    h("label", { class: "field" }, h("span", null, "Note"), note),
    err,
    submit,
  );
  const loadList = async () => {
    let rows: InviteRow[];
    try {
      rows = (await api<{ invites: InviteRow[] }>("accounts", "/dev-invites")).invites;
      sentRows = rows;
    } catch (e) {
      if (isMissingEndpoint(e) || (e instanceof ApiError && e.status === 403)) return clear(list);
      return clear(list, errorBox(e));
    }
    const used = rows.filter((r) => r.used).length;
    clear(
      list,
      h("h3", null, "Invites sent"),
      h("p", { class: "dim small" }, `${used} used · ${rows.length - used} unused`),
      rows.length
        ? h(
            "table",
            { class: "invite-table" },
            h("thead", null, h("tr", null, h("th", null, "Who"), h("th", null, "Made"), h("th", null, "Status"), h("th", null, h("span", { class: "sr-only" }, "Link")))),
            h(
              "tbody",
              null,
              rows.map((r) =>
                h(
                  "tr",
                  null,
                  h("td", null, h("strong", null, r.name), h("br"), h("span", { class: "dim small" }, `@${r.xHandle}`), r.note ? h("div", { class: "dim small" }, r.note) : null),
                  h("td", { class: "small" }, fmtDate(r.createdAt)),
                  h("td", null, r.used ? h("span", { class: "badge ok" }, r.application ? `Applied · ${STATUS_COPY[r.application.status].badge}` : `Used ${r.usedAt ? fmtDate(r.usedAt) : ""}`) : r.expired ? h("span", { class: "badge bad" }, "Expired") : h("span", { class: "badge" }, "Not used yet")),
                  h("td", null, copyButton(new URL(r.url, location.origin).href)),
                ),
              ),
            ),
          )
        : h("p", { class: "dim" }, "No invites yet."),
    );
  };
  void loadList();
  return h("section", { class: "panel invite-maker" }, h("h2", null, "Invite a developer"), h("p", { class: "dim small" }, "The link carries their name, handle and games, so their first screen already shows their work. You send the message yourself."), form, made, list);
}

function copyButton(url: string): HTMLElement {
  const b = h("button", { type: "button", class: "btn small ghost" }, "Copy");
  b.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(url);
      b.textContent = "Copied";
    } catch {
      // no clipboard (an insecure origin, a denied permission): select the link so it can be copied by hand
      const a = b.previousElementSibling;
      if (a) getSelection()?.selectAllChildren(a);
      b.textContent = "Selected";
    }
    setTimeout(() => (b.textContent = "Copy"), 2000);
  });
  return b;
}
